import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AmlCallView } from '../../phone-calls/types';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { getDrawLocationsFromPhoneCall } from '../helpers/aml';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createAmlLocationHistoryFeatureLayer } from '../helpers/feature-layers';
import { createAmlHistoryGraphic } from '../helpers/graphics';
import type { IndexedAmlLocationEntity } from '../types';
import { useLayerControl } from './use-feature-layer-control';
import { useFeatureLayerVisibility } from './use-feature-layer-visibility';

// show AML (RapidSOS) location history circles

type AmlLocationHistoryFeatureLayerProps = {
    amlCall?: AmlCallView;
    webMap?: WebMap;
    areRapidSOSAniAliLayersVisible: boolean;
};
export const useAmlLocationHistoryFeatureLayer = ({
    amlCall,
    webMap,
    areRapidSOSAniAliLayersVisible,
}: AmlLocationHistoryFeatureLayerProps) => {
    const entityIdToObjectIdMapCacheRef = useRef<
        typeof EntityIdToObjectIdMaps.AML_LOCATION_HISTORY
    >(EntityIdToObjectIdMaps.AML_LOCATION_HISTORY);

    const toDraw = useMemo(() => {
        return getDrawLocationsFromPhoneCall(amlCall);
    }, [amlCall]);

    const [amlLocationHistoryLayer, setAmlLocationHistoryLayer] = useState<FeatureLayer>();

    const createAmlLocationHistoryGraphics = useCallback(
        (indexedAmlLocationEntities: IndexedAmlLocationEntity[]) => {
            const amlLocationHistoryGraphics: Graphic[] = [];
            for (const indexedAmlLocationEntity of indexedAmlLocationEntities) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(
                    indexedAmlLocationEntity.entityId
                );
                const amlLocationHistoryGraphic = createAmlHistoryGraphic({
                    objectId,
                    indexedAmlLocationEntity,
                });
                if (amlLocationHistoryGraphic) {
                    amlLocationHistoryGraphics.push(amlLocationHistoryGraphic);
                }
            }
            return amlLocationHistoryGraphics;
        },
        []
    );

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const featureLayer = createAmlLocationHistoryFeatureLayer();

            setAmlLocationHistoryLayer(featureLayer);
            webMap.layers.add(featureLayer);
        }
    }, [webMap]);

    const didAmlLocationHistoryGraphicChange = useCallback(
        (currItem: IndexedAmlLocationEntity, prevItem: IndexedAmlLocationEntity) => {
            return currItem.entityId === prevItem.entityId && currItem.index === prevItem.index;
        },
        []
    );

    useFeatureLayerVisibility({
        featureLayer: amlLocationHistoryLayer,
        isVisible: areRapidSOSAniAliLayersVisible,
    });

    useLayerControl<IndexedAmlLocationEntity>({
        layer: amlLocationHistoryLayer,
        currItems: toDraw,
        didItemChange: didAmlLocationHistoryGraphicChange,
        createGraphics: createAmlLocationHistoryGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { amlLocationHistoryLayer };
};
